import { BOOKING_WHERE_LABELS } from '@supply-mover-2-0/shared';
import { useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BookingTimePicker } from '../components/booking/BookingTimePicker';
import { PaymentLinkShell } from '../components/paymentLinks/PaymentLinkShell';
import { useTurnstile } from '../hooks/useTurnstile';
import { formatMeeting, timezoneLabel } from '../lib/bookingTimes';
import {
	BookingRequestError,
	cancelBooking,
	fetchBooking,
	fetchRescheduleTimes,
	rescheduleBooking,
} from '../services/bookingService';
import { BookingNotice } from './BookingPage';

/**
 * PD-17646 — `/book/meeting/:token`: the link in a guest's emails, to see, move or cancel
 * their meeting without a login. The token is the only credential.
 */
export function BookingManagePage(): JSX.Element {
	const { t } = useTranslation();
	const { isDark } = useTheme();
	const queryClient = useQueryClient();
	const { token = '' } = useParams();

	const { data: booking, isLoading } = useQuery({
		queryKey: ['booking-meeting', token],
		queryFn: () => fetchBooking(token),
		retry: false,
	});

	const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
	const [mode, setMode] = useState<'view' | 'move' | 'cancel'>('view');
	const [picked, setPicked] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState<string | null>(null);

	const turnstile = useTurnstile({
		siteKey: mode !== 'view' ? (booking?.turnstileSiteKey ?? null) : null,
		action: 'booking',
		theme: isDark ? 'dark' : 'light',
	});

	const fetchTimes = useCallback((window: { from: string; to: string }) => fetchRescheduleTimes(token, window), [token]);
	const pickerKey = useMemo(() => ['manage', token], [token]);

	const save = async (action: () => Promise<unknown>): Promise<void> => {
		setSaving(true);
		setMessage(null);

		try {
			await action();
			await queryClient.invalidateQueries({ queryKey: ['booking-meeting', token] });
			await queryClient.invalidateQueries({ queryKey: ['booking-times'] });
			setMode('view');
			setPicked(null);
		} catch (caught) {
			turnstile.reset();
			setMessage(caught instanceof BookingRequestError ? caught.message : 'Something went wrong. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	if (isLoading) {
		return <PaymentLinkShell secureLabel={null} showSignIn={false}><div className="min-h-64" /></PaymentLinkShell>;
	}

	if (booking == null) {
		return <BookingNotice title="This meeting could not be found" body="Check that you opened the whole link from your email." />;
	}

	const cancelled = booking.status === 'cancelled';
	const where = booking.whereDetail ?? BOOKING_WHERE_LABELS[booking.whereKind];

	return (
		<PaymentLinkShell secureLabel={null} showSignIn={false} wide={mode === 'move'}>
			<div className="space-y-4">
				<section className="bg-brand-surface border border-brand-border rounded-xl p-6 space-y-2" data-testid="booking-meeting">
					<p className="text-sm text-brand-muted">
						{cancelled ? t('Cancelled') : booking.status === 'rescheduled' ? t('Moved') : t('Booked')}
					</p>
					<h1 className="text-xl font-bold text-default">{t(booking.title)}</h1>
					<p className="text-default">
						{t('with')} {t(booking.hostName)}, {t(booking.companyName)}
					</p>
					<p className={cancelled ? 'text-brand-muted line-through' : 'text-default font-medium'}>
						{t(formatMeeting(booking.start, timezone))}
					</p>
					<p className="text-xs text-brand-muted">{t(timezoneLabel(timezone))}</p>
					{booking.joinUrl != null && !cancelled ? (
						<a href={booking.joinUrl} className="text-portal-accent hover:underline">
							{t('Join the meeting')}
						</a>
					) : (
						<p className="text-sm text-brand-muted">{t(where)}</p>
					)}
					{booking.canChange && mode === 'view' ? (
						<div className="flex gap-2 pt-2">
							<button
								type="button"
								onClick={() => {
									setMode('move');
								}}
								className="rounded-lg bg-portal-accent hover:bg-portal-accent-hover text-white px-4 py-2 text-sm font-semibold"
							>
								{t('Reschedule')}
							</button>
							<button
								type="button"
								onClick={() => {
									setMode('cancel');
								}}
								className="rounded-lg border border-brand-border text-default px-4 py-2 text-sm font-semibold"
							>
								{t('Cancel meeting')}
							</button>
						</div>
					) : null}
				</section>
				{mode === 'move' ? (
					<>
						<div ref={turnstile.containerRef} />
						{message != null ? (
							<p className="text-sm text-default" role="alert">
								{t(message)}
							</p>
						) : null}
						<div className="flex gap-2">
							<button
								type="button"
								disabled={picked == null || saving}
								onClick={() => {
									void save(async () => {
										await rescheduleBooking(token, {
											start: picked ?? '',
											timezone,
											turnstileToken: await turnstile.getToken(),
										});
									});
								}}
								className="rounded-lg bg-portal-accent hover:bg-portal-accent-hover text-white px-4 py-2 text-sm font-semibold disabled:opacity-60"
							>
								{picked != null ? t('Move to {{time}}', { time: formatMeeting(picked, timezone) }) : t('Pick a new time')}
							</button>
							<button
								type="button"
								onClick={() => {
									setMode('view');
									setPicked(null);
								}}
								className="rounded-lg border border-brand-border text-default px-4 py-2 text-sm font-semibold"
							>
								{t('Keep this time')}
							</button>
						</div>
						<BookingTimePicker
							queryKey={pickerKey}
							fetchTimes={fetchTimes}
							hostTimezone={timezone}
							timezone={timezone}
							onTimezoneChange={setTimezone}
							selected={picked}
							onSelect={setPicked}
						/>
					</>
				) : null}
				{mode === 'cancel' ? (
					<section className="bg-brand-surface border border-brand-border rounded-xl p-6 space-y-3">
						<p className="text-default">{t('Cancel this meeting?')}</p>
						<div ref={turnstile.containerRef} />
						{message != null ? (
							<p className="text-sm text-default" role="alert">
								{t(message)}
							</p>
						) : null}
						<div className="flex gap-2">
							<button
								type="button"
								disabled={saving}
								onClick={() => {
									void save(async () => {
										await cancelBooking(token, { turnstileToken: await turnstile.getToken() });
									});
								}}
								className="rounded-lg bg-portal-accent hover:bg-portal-accent-hover text-white px-4 py-2 text-sm font-semibold disabled:opacity-60"
							>
								{t('Cancel meeting')}
							</button>
							<button
								type="button"
								onClick={() => {
									setMode('view');
								}}
								className="rounded-lg border border-brand-border text-default px-4 py-2 text-sm font-semibold"
							>
								{t('Keep it')}
							</button>
						</div>
					</section>
				) : null}
			</div>
		</PaymentLinkShell>
	);
}
